import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CloseCircleOutlined,
  CopyOutlined,
  EditableOutlined,
  HorizontalDragUniversal,
  MoreHorizontalUniversal,
  NoAccessUniversal,
  TickMarkOutlined,
} from '@duxton-web/icons';
import { Button, Popover } from '@duxton-web/ui';
import scribe from '@grab-x/scribe-web-sdk';

import Abort from '../../detail/footer/abort';
import Approve from '../../detail/footer/approve';
import Clone from '../../detail/footer/clone';
import Edit from '../../detail/footer/edit';
import Reject from '../../detail/footer/reject';
import { AccessControlWrapper } from '@/common/components';
import { scribeInstrumentationEvents } from '@/common/Scribe/EventsList';
import {
  ABORTED,
  ADAPTIVE,
  CANCELLED,
  COMPLETED,
  EXPERIMENT,
  REQUESTED,
  SCHEDULED,
  STARTED,
} from '@/constants';
import { useExperimentContext } from '@/contexts/ExperimentContext';
import { SuperExperiment } from '@/lib/PlannerDTOs';
import { AccessTarget, UserAction } from '@/utils/accessControl';

interface ExperimentListingActionsProps {
  experiment: SuperExperiment;
  handleReject: (experiment: SuperExperiment) => void;
  handleEdit: (experiment: SuperExperiment) => void;
  handleClone: (experiment: SuperExperiment) => void;
  handleAbort: (experiment: SuperExperiment) => void;
  handlePause: (experiment: SuperExperiment) => void;
  handleApprove: () => void;
}

const ExperimentListingActions = ({
  experiment,
  handleReject,
  handleEdit,
  handleClone,
  handleAbort,
  handlePause,
  handleApprove,
}: ExperimentListingActionsProps) => {
  const { getAccessDataForExperiment } = useExperimentContext();
  const [shouldLoadAccessDetails, setShouldLoadAccessDetails] = useState(false);
  const [techFamilies, setTechFamilies] = useState<string[]>([]);
  const [accessTargets, setAccessTargets] = useState<AccessTarget[]>([]);

  useEffect(() => {
    if (!shouldLoadAccessDetails) return;

    getAccessDataForExperiment(experiment).then(
      ({ techFamilies, accessTargets }) => {
        setTechFamilies(techFamilies);
        setAccessTargets(accessTargets);
      },
    );
  }, [experiment, getAccessDataForExperiment, shouldLoadAccessDetails]);

  const trackMoreOptionClick = useCallback(() => {
    setShouldLoadAccessDetails(true);
    scribe.sendEvent(
      scribeInstrumentationEvents.clickExperimentMoreOption.name,
      {
        experiment: experiment.name,
        status: experiment.state,
        STATE_NAME: scribeInstrumentationEvents.clickExperimentMoreOption.state,
      },
    );
  }, [experiment.name, experiment.state]);

  const getExperimentActions = ({
    record,
    handleReject,
    handleEdit,
    handleClone,
    handleAbort,
    handlePause,
    handleApprove,
  }) => {
    const renderCloneButton = useCallback(
      (record) => {
        const experimentStrategy = record?.variables?.[0]?.strategy;

        return (
          <Clone
            ghost
            variableTechFamilies={techFamilies}
            experiment={record?.type === EXPERIMENT ? record : null}
            adaptiveExperiment={record?.type === ADAPTIVE ? record : null}
            icon={<CopyOutlined />}
            experimentStrategy={experimentStrategy}
            accessTargets={accessTargets}
          />
        );
      },
      [accessTargets, techFamilies, handleClone],
    );

    const renderAbortButton = useCallback(
      (record) => {
        return (
          <Abort
            ghost
            variableTechFamilies={techFamilies}
            experiment={record?.type === EXPERIMENT ? record : null}
            adaptiveExperiment={record?.type === ADAPTIVE ? record : null}
            icon={<NoAccessUniversal />}
            accessTargets={accessTargets}
          />
        );
      },
      [accessTargets, techFamilies, handleAbort],
    );

    const renderPauseButton = useCallback(
      (record) => {
        return (
          <AccessControlWrapper
            action={UserAction.PAUSE_CURRENT_EXPERIMENT}
            techFamilies={techFamilies}
            accessTargets={accessTargets}
            tooltipProps={{
              placement: 'left',
            }}
          >
            {(hasAccess) => (
              <Button
                size="small"
                ghost
                shape="default"
                icon={<HorizontalDragUniversal />}
                disabled={!hasAccess}
                onClick={() => {
                  handlePause(record);
                }}
              >
                Pause
              </Button>
            )}
          </AccessControlWrapper>
        );
      },
      [accessTargets, techFamilies, handlePause],
    );

    const renderRejectButton = useCallback(
      (record) => {
        return (
          <Reject
            ghost
            variableTechFamilies={techFamilies}
            experiment={record?.type === EXPERIMENT ? record : null}
            adaptiveExperiment={record?.type === ADAPTIVE ? record : null}
            icon={<CloseCircleOutlined />}
            accessTargets={accessTargets}
          />
        );
      },
      [accessTargets, techFamilies, handleReject],
    );

    const renderApproveButton = useCallback(
      (record) => {
        return (
          <Approve
            ghost
            variableTechFamilies={techFamilies}
            experiment={record?.type === EXPERIMENT ? record : null}
            adaptiveExperiment={record?.type === ADAPTIVE ? record : null}
            icon={<TickMarkOutlined />}
            accessTargets={accessTargets}
          />
        );
      },
      [accessTargets, techFamilies, handleApprove],
    );

    const renderEditButton = useCallback(
      (record) => {
        return (
          <Edit
            ghost
            variableTechFamilies={techFamilies}
            experiment={record?.type === EXPERIMENT ? record : null}
            adaptiveExperiment={record?.type === ADAPTIVE ? record : null}
            icon={<EditableOutlined />}
            text="Edit"
            accessTargets={accessTargets}
          />
        );
      },
      [accessTargets, techFamilies, handleEdit],
    );

    return useMemo(() => {
      switch (record.state) {
        case REQUESTED:
          return [
            renderApproveButton(record),
            renderRejectButton(record),
            <div
              key="divider1"
              style={{
                background: '#F7F7F7',
                width: '100%',
                height: '1px',
              }}
            />,
            renderEditButton(record),
            renderCloneButton(record),
            <div
              key="divider2"
              style={{
                background: '#F7F7F7',
                width: '100%',
                height: '1px',
              }}
            />,
            renderAbortButton(record),
          ];
        case SCHEDULED:
        case STARTED: {
          if (record?.type === EXPERIMENT) {
            return [
              renderEditButton(record),
              renderCloneButton(record),
              <div
                key="divider2"
                style={{
                  background: '#F7F7F7',
                  width: '100%',
                  height: '1px',
                }}
              />,
              renderAbortButton(record),
            ];
          }

          return [renderPauseButton(record), renderAbortButton(record)];
        }
        case COMPLETED:
        case CANCELLED:
        case ABORTED: {
          if (record?.type !== EXPERIMENT) return null;

          return [renderCloneButton(record)];
        }
        default:
          return null;
      }
    }, [
      record.state,
      record.type,
      renderApproveButton,
      renderRejectButton,
      renderEditButton,
      renderCloneButton,
      renderAbortButton,
      renderPauseButton,
    ]);
  };

  const actions = getExperimentActions({
    record: experiment,
    handleReject,
    handleEdit,
    handleClone,
    handleAbort,
    handlePause,
    handleApprove,
  });

  if (!actions) {
    return (
      <Button
        size="small"
        type="link"
        shape="default"
        disabled
        icon={<MoreHorizontalUniversal />}
        onClick={trackMoreOptionClick}
      />
    );
  }

  return (
    <Popover
      trigger="click"
      placement="left"
      showArrow={false}
      content={
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 8,
            width: 110,
          }}
        >
          {actions}
        </div>
      }
    >
      <Button
        size="small"
        type="link"
        shape="default"
        icon={<MoreHorizontalUniversal />}
        onClick={trackMoreOptionClick}
      />
    </Popover>
  );
};

export default ExperimentListingActions;
